import React from "react";
import ArrowLeftToRight from "../../../image/icons/map/details/ArrowLeftToRight.svg?react";
import Clock from "../../../image/icons/map/details/ClockIcon.svg?react";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { formatTime } from "../../../tools/TimeRelated";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { formatItineraryTime } from "./itineraryDetails.tools";

/** Visual treatment for the timing block in the itinerary panel chrome row. */
type ItineraryDetailsTimingPillVariant = "default" | "embedded";

/**
 * Props for the timing block shown in the itinerary panel chrome row.
 */
interface ItineraryDetailsTimingPillProps {

    /** Selected itinerary whose duration and clock times are displayed. */
    "itinerary": ItineraryDTO;

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi": boolean;

    /**
     * `default` — standalone bordered pill (legacy).
     * `embedded` — borderless segment inside the unified timing/save capsule.
     */
    "variant"?: ItineraryDetailsTimingPillVariant;
}

/**
 * Renders itinerary duration and start/end clock times for the itinerary-details chrome row.
 *
 * @remarks In `embedded` mode the layout collapses to a single horizontal line so the timing
 * block can share a capsule with the save toggle without adding vertical height.
 */
const ItineraryDetailsTimingPill: React.FC<ItineraryDetailsTimingPillProps> = ( {
    itinerary,
    isDarkUi,
    variant = "default",
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const accent = palette.itineraryDetailsTimingPillAccent;
    const startTimeLabel = formatItineraryTime( new Date( itinerary.startTime ).getTime() );
    const endTimeLabel = formatItineraryTime( new Date( itinerary.endTime ).getTime() );
    const durationLabel = formatTime( itinerary.duration ?? 0, true );

    if ( variant === "embedded" ) {
        return (
            <div
                className="inline-flex h-9 min-w-0 items-center gap-1.5 px-2"
                style={{ "color": accent }}
            >
                <Clock
                    aria-hidden={true}
                    className="block h-3.5 w-3.5 shrink-0"
                    style={{ "color": accent }}
                />
                <span className="shrink-0 text-sm font-black leading-none">
                    {durationLabel}
                </span>
                <span
                    aria-hidden={true}
                    className="h-1 w-1 shrink-0 self-center rounded-full opacity-60"
                    style={{ "backgroundColor": accent }}
                />
                <span
                    className="inline-flex min-w-0 items-center gap-1 self-center text-[10px] font-semibold leading-none tabular-nums"
                    style={{ "color": palette.overlayTextSecondary }}
                >
                    <span className="leading-none">
                        {startTimeLabel}
                    </span>
                    <ArrowLeftToRight
                        aria-hidden={true}
                        className="block h-2.5 w-2 shrink-0"
                        style={{ "color": palette.overlayTextSecondary }}
                    />
                    <span className="leading-none">
                        {endTimeLabel}
                    </span>
                </span>
            </div>
        );
    }

    return (
        <div
            className="inline-flex min-w-0 items-center gap-1 rounded-lg border-2 border-solid px-1 py-1 leading-tight"
            style={{
                "borderColor": accent,
                "color":       accent,
            }}
        >
            <Clock
                aria-hidden={true}
                className="inline-block h-4 w-4 shrink-0 self-center"
                style={{ "color": accent }}
            />
            <div className="inline-flex min-w-0 flex-col">
                <span className="truncate text-[11px] font-bold">
                    {durationLabel}
                </span>
                <span className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold opacity-90">
                    <span className="tabular-nums">
                        {startTimeLabel}
                    </span>
                    <ArrowLeftToRight
                        aria-hidden={true}
                        className="inline-block w-2.5 shrink-0"
                        style={{ "color": accent }}
                    />
                    <span className="tabular-nums">
                        {endTimeLabel}
                    </span>
                </span>
            </div>
        </div>
    );
};

export default ItineraryDetailsTimingPill;
